import { useCallback, useEffect, useRef } from 'react'
import { useAuth } from '@/store/auth.store'

import { SocketEvent, Task } from '@scrambl/shared'

import { useRealtime } from '../core/useRealtime'

// Add task events to broadcast types
export enum TaskBroadcastEventType {
  TASK_CREATED = 'task:created',
  TASK_UPDATED = 'task:updated',
  TASK_DELETED = 'task:deleted',
  TASK_STATUS_UPDATED = 'task:status_updated',
  TASK_ASSIGNED = 'task:assigned',
  TASK_UNASSIGNED = 'task:unassigned',
  RECURRING_TASK_CREATED = 'task:recurring_created',
}

export interface TaskRealtimeOptions {
  onTaskCreated?: (task: Task) => void
  onTaskUpdated?: (task: Task) => void
  onTaskDeleted?: (data: { id: string; organizationId: string }) => void
  onTaskStatusChanged?: (data: {
    taskId: string
    oldStatus: string
    newStatus: string
    task: Task
  }) => void
  onTaskAssigned?: (data: {
    taskId: string
    userId: string
    task: Task
  }) => void
  onTaskUnassigned?: (data: {
    taskId: string
    userId: string
    task: Task
  }) => void
  onRecurringTaskCreated?: (data: {
    parentTaskId: string
    newTask: Task
    instanceNumber: number
  }) => void
  enableBroadcastChannel?: boolean
  enabled?: boolean
}

/**
 * Hook for real-time task synchronization
 * Listens to task events for the current organization and user
 */
export function useTaskRealtime(options: TaskRealtimeOptions = {}) {
  const {
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTaskStatusChanged,
    onTaskAssigned,
    onTaskUnassigned,
    onRecurringTaskCreated,
    enableBroadcastChannel = true,
    enabled = true,
  } = options

  const { user } = useAuth.getState()
  const userId = user?.id

  const {
    socket,
    isReady,
    joinRoom,
    leaveRoom,
    addEventListener,
    processSocketEvent,
    hasProcessedEvent,
    markEventProcessed,
  } = useRealtime({
    enableBroadcastChannel,
    suppressLogging: false, // Always log task events as they're critical for debugging
  })

  // Keep refs for callbacks to avoid re-subscribing
  const callbackRefs = useRef({
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTaskStatusChanged,
    onTaskAssigned,
    onTaskUnassigned,
    onRecurringTaskCreated,
  })

  // Update refs when callbacks change
  useEffect(() => {
    callbackRefs.current = {
      onTaskCreated,
      onTaskUpdated,
      onTaskDeleted,
      onTaskStatusChanged,
      onTaskAssigned,
      onTaskUnassigned,
      onRecurringTaskCreated,
    }
  }, [
    onTaskCreated,
    onTaskUpdated,
    onTaskDeleted,
    onTaskStatusChanged,
    onTaskAssigned,
    onTaskUnassigned,
    onRecurringTaskCreated,
  ])

  // Join the user's room when ready
  useEffect(() => {
    if (!enabled || !isReady || !userId) {
      return
    }

    // Join user room to receive task events
    // Note: User rooms are automatically joined by backend, so joinRoom may return false
    const joined = joinRoom('user', userId)
    if (!joined) {
      // This is expected behavior - user rooms are auto-joined by backend
      // Debug only
    } else {
      // Debug only
    }

    return () => {
      leaveRoom('user', userId)
    }
  }, [enabled, isReady, userId, joinRoom, leaveRoom])

  // Socket event handlers
  const handleTaskCreated = useCallback(
    (data: Task) => {
      // Debug only
      processSocketEvent(SocketEvent.TASK_CREATED, 'task', data.id, data)
      callbackRefs.current.onTaskCreated?.(data)
    },
    [processSocketEvent],
  )

  const handleTaskUpdated = useCallback(
    (data: Task) => {
      // Debug only
      processSocketEvent(SocketEvent.TASK_UPDATED, 'task', data.id, data)
      callbackRefs.current.onTaskUpdated?.(data)

      // REMOVED: Double triggering status change from task update
      // This was causing event spam - onTaskUpdated already handles status changes
      // The separate onTaskStatusChanged handler is only for standalone status events
    },
    [processSocketEvent],
  )

  const handleTaskDeleted = useCallback(
    (data: { id: string; organizationId: string }) => {
      // Debug only
      processSocketEvent(SocketEvent.TASK_DELETED, 'task', data.id, data)
      callbackRefs.current.onTaskDeleted?.(data)
    },
    [processSocketEvent],
  )

  const handleTaskStatusChanged = useCallback(
    (data: any) => {
      // Debug only
      // The backend sends different data structure, let's handle both
      const taskId = data.taskId || data.id
      // Debug only
      processSocketEvent(SocketEvent.TASK_STATUS_UPDATED, 'task', taskId, data)
      callbackRefs.current.onTaskStatusChanged?.(data)
    },
    [processSocketEvent],
  )

  const handleTaskAssigned = useCallback(
    (data: { taskId: string; userId: string; task: Task }) => {
      // Debug only
      processSocketEvent(SocketEvent.TASK_ASSIGNED, 'task', data.taskId, data)
      callbackRefs.current.onTaskAssigned?.(data)
    },
    [processSocketEvent],
  )

  const handleTaskUnassigned = useCallback(
    (data: { taskId: string; userId: string; task: Task }) => {
      // Debug only
      processSocketEvent(SocketEvent.TASK_UNASSIGNED, 'task', data.taskId, data)
      callbackRefs.current.onTaskUnassigned?.(data)
    },
    [processSocketEvent],
  )

  const handleRecurringTaskCreated = useCallback(
    (data: { parentTaskId: string; newTask: Task; instanceNumber: number }) => {
      // Debug only
      processSocketEvent(
        SocketEvent.RECURRING_TASK_CREATED,
        'task',
        data.newTask.id,
        data,
      )
      callbackRefs.current.onRecurringTaskCreated?.(data)
    },
    [processSocketEvent],
  )

  // Set up socket listeners
  useEffect(() => {
    if (!enabled || !socket || !isReady) {
      return
    }

    // Debug only

    // Add socket event listeners
    socket.on(SocketEvent.TASK_CREATED, handleTaskCreated)
    socket.on(SocketEvent.TASK_UPDATED, handleTaskUpdated)
    socket.on(SocketEvent.TASK_DELETED, handleTaskDeleted)
    socket.on(SocketEvent.TASK_STATUS_UPDATED, handleTaskStatusChanged)
    socket.on(SocketEvent.TASK_ASSIGNED, handleTaskAssigned)
    socket.on(SocketEvent.TASK_UNASSIGNED, handleTaskUnassigned)
    socket.on(SocketEvent.RECURRING_TASK_CREATED, handleRecurringTaskCreated)

    return () => {
      socket.off(SocketEvent.TASK_CREATED, handleTaskCreated)
      socket.off(SocketEvent.TASK_UPDATED, handleTaskUpdated)
      socket.off(SocketEvent.TASK_DELETED, handleTaskDeleted)
      socket.off(SocketEvent.TASK_STATUS_UPDATED, handleTaskStatusChanged)
      socket.off(SocketEvent.TASK_ASSIGNED, handleTaskAssigned)
      socket.off(SocketEvent.TASK_UNASSIGNED, handleTaskUnassigned)
      socket.off(SocketEvent.RECURRING_TASK_CREATED, handleRecurringTaskCreated)
    }
  }, [
    enabled,
    socket,
    isReady,
    handleTaskCreated,
    handleTaskUpdated,
    handleTaskDeleted,
    handleTaskStatusChanged,
    handleTaskAssigned,
    handleTaskUnassigned,
    handleRecurringTaskCreated,
  ])

  // Set up broadcast channel listeners
  useEffect(() => {
    if (!enabled || !enableBroadcastChannel) {
      return
    }

    // Add broadcast event listeners for cross-tab sync
    const cleanupFns: (() => void)[] = []

    cleanupFns.push(
      addEventListener(TaskBroadcastEventType.TASK_CREATED, data => {
        const task = data as Task
        const eventKey = `${SocketEvent.TASK_CREATED}-${task.id}`
        if (hasProcessedEvent(eventKey)) {
          return
        }
        markEventProcessed(eventKey)
        callbackRefs.current.onTaskCreated?.(task)
      }),
    )

    cleanupFns.push(
      addEventListener(TaskBroadcastEventType.TASK_UPDATED, data => {
        const task = data as Task
        const eventKey = `${SocketEvent.TASK_UPDATED}-${task.id}`
        if (hasProcessedEvent(eventKey)) {
          return
        }
        markEventProcessed(eventKey)
        callbackRefs.current.onTaskUpdated?.(task)
      }),
    )

    cleanupFns.push(
      addEventListener(TaskBroadcastEventType.TASK_DELETED, data => {
        const deleteData = data as { id: string; organizationId: string }
        const eventKey = `${SocketEvent.TASK_DELETED}-${deleteData.id}`
        if (hasProcessedEvent(eventKey)) {
          return
        }
        markEventProcessed(eventKey)
        callbackRefs.current.onTaskDeleted?.(deleteData)
      }),
    )

    cleanupFns.push(
      addEventListener(TaskBroadcastEventType.RECURRING_TASK_CREATED, data => {
        const recurringData = data as {
          parentTaskId: string
          newTask: Task
          instanceNumber: number
        }
        const eventKey = `${SocketEvent.RECURRING_TASK_CREATED}-${recurringData.newTask.id}`
        if (hasProcessedEvent(eventKey)) {
          return
        }
        markEventProcessed(eventKey)
        callbackRefs.current.onRecurringTaskCreated?.(recurringData)
      }),
    )

    return () => {
      cleanupFns.forEach(cleanup => cleanup())
    }
  }, [
    enabled,
    addEventListener,
    hasProcessedEvent,
    markEventProcessed,
    enableBroadcastChannel,
  ])

  return {
    isReady,
  }
}
